import React, { useEffect } from 'react';
import { franchiseFormats, faqFranchise } from '../data/mock';
import { Check, ArrowRight, Download } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Link } from 'react-router-dom';
import { trackDownload } from '../utils/analytics';

const Franchise = () => {
  const franchiseModels = [
    {
      id: 1,
      title: 'Silver Model',
      subtitle: 'FOFO - Franchise Owner, Franchise Operated',
      investment: '₹15 Lakhs',
      description: 'Owner-operated model with complete autonomy and central support for hands-on entrepreneurs.',
      details: {
        capex: [
          { item: 'Franchise Fee', amount: '₹2,00,000' },
          { item: 'Fixture & Furniture', amount: '₹3,50,000' },
          { item: 'Interior Work', amount: '₹1,50,000' },
          { item: 'Electrical Work', amount: '₹1,50,000' },
          { item: 'Computer & Software', amount: '₹1,00,000' },
          { item: 'Signage', amount: '₹50,000' }
        ],
        opex: [
          { item: 'Security Deposit', amount: '₹1,00,000' },
          { item: 'Stock', amount: '₹4,00,000' }
        ],
        profit: {
          margin: '10-15% Gross Margin',
          note: 'On all products'
        }
      },
      benefits: [
        'Full ownership & operational control',
        'Central procurement support',
        '35,000+ SKU access',
        'Training & compliance support',
        '10-15% profit margin',
        'Established brand presence'
      ]
    },
    {
      id: 2,
      title: 'Gold Model',
      subtitle: 'FICO - Franchise Invest, Company Operated',
      investment: '₹35 Lakhs',
      description: 'Invest and earn returns while Care Pharmacy handles all daily operations with minimum guarantee.',
      details: {
        capex: [
          { item: 'Total Investment', amount: '₹35,00,000' }
        ],
        sales: {
          monthly: '₹30,00,000',
          earnings: '₹3,00,000 per month'
        },
        profit: {
          year5Total: '₹3.50 Crores',
          note: 'Projected returns over 5 years'
        }
      },
      benefits: [
        'Passive investment opportunity',
        'Company-managed operations',
        'Minimum guarantee returns',
        '₹3 Lakh monthly earnings potential',
        'Regular audits & reporting',
        'No operational involvement needed'
      ]
    },
    {
      id: 3,
      title: 'Platinum Model',
      subtitle: 'FICO - Franchise Invest, Company Operated',
      investment: '₹60 Lakhs',
      description: 'Premium flagship model with highest returns, fully managed by Care Pharmacy with assured ROI.',
      details: {
        capex: [
          { item: 'Total Investment', amount: '₹60,00,000' }
        ],
        sales: {
          monthly: '₹50,00,000',
          earnings: '₹5,00,000 per month'
        },
        profit: {
          year5Total: '₹6.00 Crores',
          note: 'Projected returns over 5 years'
        }
      },
      benefits: [
        'Premium flagship locations',
        'Maximum earning potential',
        '₹5 Lakh monthly earnings',
        'Complete operational management',
        'Priority support & monitoring',
        'Highest ROI in portfolio'
      ]
    }
  ];

  useEffect(() => {
    // Load Instagram embed script
    const script = document.createElement('script');
    script.src = '//www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              Build With India's Fast-Emerging Pharmacy Brand
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '48px'
            }}>
              Plug-and-play formats with 190+ outlets, centralised procurement, comprehensive training, and minimum-guarantee returns for investors.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary">
                Apply for Franchise
                <ArrowRight size={16} />
              </Link>
              <a 
                href="/care-pharmacy-brochure.pdf" 
                download="Care-Pharmacy-Franchise-Brochure.pdf"
                className="btn-primary"
                style={{ 
                  background: 'var(--brand-green)',
                  color: 'var(--bg-primary)',
                  border: '1px solid var(--brand-green)'
                }}
                onClick={() => trackDownload('Care-Pharmacy-Franchise-Brochure.pdf', 'pdf')}
              >
                <Download size={16} />
                Download Brochure
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Testimonial Video with Stats */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Hear From Our Founder Brijesh Patel
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 60px'
          }}>
            Successful Journey of Brijesh Patel is Inspired us to Become Partner with Care Pharmacy.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '60px',
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }} className="testimonial-grid">
            {/* Portrait Video */}
            <div style={{
              maxWidth: '400px',
              width: '100%'
            }}>
              <video
                controls
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                }}
              >
                <source src="/franchise-testimonial-video.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Statistics & Details */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '40px'
            }}>
              <div>
                <h3 className="hero-medium" style={{ marginBottom: '32px' }}>
                  Why Partner With Care Pharmacy?
                </h3>
                <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '40px' }}>
                  Join India's fastest-growing pharmacy retail chain with proven success, comprehensive support, and guaranteed returns.
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '32px',
                width: '100%'
              }} className="stats-grid">
                <div style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: '300',
                    color: 'var(--brand-blue)',
                    marginBottom: '8px',
                    lineHeight: 1
                  }}>
                    190+
                  </div>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Outlets PAN India
                  </p>
                </div>

                <div style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: '300',
                    color: 'var(--brand-green)',
                    marginBottom: '8px',
                    lineHeight: 1
                  }}>
                    35,000+
                  </div>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Product Portfolio
                  </p>
                </div>

                <div style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: '300',
                    color: 'var(--brand-blue)',
                    marginBottom: '8px',
                    lineHeight: 1
                  }}>
                    10-85%
                  </div>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Discount on Medicines
                  </p>
                </div>

                <div style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: '300',
                    color: 'var(--brand-green)',
                    marginBottom: '8px',
                    lineHeight: 1
                  }}>
                    ₹10 Cr+
                  </div>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Customer Savings
                  </p>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            @media (max-width: 1024px) {
              .testimonial-grid {
                grid-template-columns: 1fr !important;
                gap: 40px !important;
                width: 100% !important;
                max-width: 100% !important;
              }
              .testimonial-grid > div:first-child {
                max-width: 100% !important;
                margin: 0 auto;
                width: 100% !important;
              }
              .stats-grid {
                grid-template-columns: 1fr !important;
                gap: 20px !important;
                width: 100% !important;
              }
            }
            @media (max-width: 640px) {
              .testimonial-grid {
                gap: 20px !important;
                padding: 0 !important;
              }
              .stats-grid {
                gap: 16px !important;
              }
              .franchise-model-grid {
                grid-template-columns: 1fr !important;
                gap: 24px !important;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Franchise Models */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Choose Your Investment Model
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 60px'
          }}>
            Three distinct models designed to suit different investment capacities and involvement levels — from hands-on operation to passive investment.
          </p>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '40px'
          }}>
            {franchiseModels.map((model) => (
              <div key={model.id} className="hover-lift" style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                border: model.id === 2 ? '2px solid var(--brand-green)' : '1px solid var(--border-light)',
                overflow: 'hidden'
              }}>
                {model.id === 2 && (
                  <div style={{
                    background: 'var(--brand-green)',
                    color: 'white',
                    padding: '8px 24px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '500',
                    letterSpacing: '0.5px'
                  }}>
                    MOST POPULAR
                  </div>
                )}
                <div style={{ padding: '40px' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '40px',
                    alignItems: 'start',
                    width: '100%'
                  }} className="franchise-model-grid">
                    {/* Model Header */}
                    <div>
                      <div style={{
                        fontSize: '48px',
                        fontWeight: '300',
                        color: model.id === 2 ? 'var(--brand-green)' : 'var(--brand-blue)',
                        marginBottom: '8px',
                        lineHeight: 1
                      }}>
                        {model.title}
                      </div>
                      <div className="body-regular" style={{
                        color: 'var(--text-secondary)',
                        marginBottom: '16px'
                      }}>
                        {model.subtitle}
                      </div>
                      <div className="hero-medium" style={{
                        color: 'var(--text-primary)',
                        marginBottom: '16px'
                      }}>
                        {model.investment}
                      </div>
                      <p className="body-regular" style={{
                        color: 'var(--text-secondary)',
                        marginBottom: '24px'
                      }}>
                        {model.description}
                      </p>
                      
                      {/* Benefits */}
                      <h3 className="heading-3" style={{ marginBottom: '16px' }}>Key Benefits</h3>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                      }}>
                        {model.benefits.map((benefit, index) => (
                          <li key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <Check size={18} style={{ color: 'var(--brand-green)', flexShrink: 0, marginTop: '2px' }} />
                            <span className="body-regular">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Investment Details */}
                    <div style={{
                      background: 'var(--bg-primary)',
                      padding: '32px',
                      borderRadius: '0px'
                    }}>
                      <h3 className="heading-3" style={{ marginBottom: '24px' }}>Investment Breakdown</h3>
                      
                      {/* CAPEX */}
                      <div style={{ marginBottom: '24px' }}>
                        <div className="body-small" style={{
                          color: 'var(--text-meta)',
                          fontWeight: '500',
                          marginBottom: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Capital Expenditure
                        </div>
                        {model.details.capex.map((item, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 0',
                            borderBottom: index < model.details.capex.length - 1 ? '1px solid var(--border-light)' : 'none'
                          }}>
                            <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                              {item.item}
                            </span>
                            <span className="body-regular" style={{ fontWeight: '500' }}>
                              {item.amount}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* OPEX (for Silver model) */}
                      {model.details.opex && (
                        <div style={{ marginBottom: '24px' }}>
                          <div className="body-small" style={{
                            color: 'var(--text-meta)',
                            fontWeight: '500',
                            marginBottom: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Operational Expenditure
                          </div>
                          {model.details.opex.map((item, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '8px 0',
                              borderBottom: index < model.details.opex.length - 1 ? '1px solid var(--border-light)' : 'none'
                            }}>
                              <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                                {item.item}
                              </span>
                              <span className="body-regular" style={{ fontWeight: '500' }}>
                                {item.amount}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Sales (for Gold/Platinum) */}
                      {model.details.sales && (
                        <div style={{
                          marginBottom: '24px',
                          padding: '20px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '0px'
                        }}>
                          <div className="body-small" style={{
                            color: 'var(--text-meta)',
                            fontWeight: '500',
                            marginBottom: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Expected Sales
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <div className="heading-3" style={{ color: 'var(--brand-blue)' }}>
                              {model.details.sales.monthly}
                            </div>
                            <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              Monthly Sales
                            </div>
                          </div>
                          <div>
                            <div className="heading-2" style={{ color: 'var(--brand-green)' }}>
                              {model.details.sales.earnings}
                            </div>
                            <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              Monthly Earnings
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Profit */}
                      <div style={{
                        padding: '20px',
                        background: model.id === 1 ? 'var(--bg-secondary)' : 'linear-gradient(135deg, rgba(124, 179, 66, 0.1), rgba(37, 99, 235, 0.1))',
                        borderRadius: '0px',
                        border: model.id !== 1 ? '1px solid var(--brand-green)' : 'none'
                      }}>
                        <div className="body-small" style={{
                          color: 'var(--text-meta)',
                          fontWeight: '500',
                          marginBottom: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {model.id === 1 ? 'Profit Margin' : 'Projected Returns'}
                        </div>
                        {model.id === 1 ? (
                          <>
                            <div className="heading-2" style={{ color: 'var(--brand-blue)', marginBottom: '4px' }}>
                              {model.details.profit.margin}
                            </div>
                            <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              {model.details.profit.note}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="hero-medium" style={{ color: 'var(--brand-green)', marginBottom: '4px' }}>
                              {model.details.profit.year5Total}
                            </div>
                            <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              {model.details.profit.note}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auto Business Ecosystem */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Auto Business Ecosystem
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 60px'
          }}>
            Our comprehensive support system ensures your franchise runs smoothly from day one with minimal operational complexity.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              { 
                title: 'Site Selection & Setup', 
                description: 'Expert guidance on location selection, catchment analysis, store layout design, and complete setup assistance.' 
              },
              { 
                title: 'Established Brand Power', 
                description: 'Leverage our 190+ outlet network, proven track record, and strong brand recognition in the pharmacy retail space.' 
              },
              { 
                title: 'Central Procurement', 
                description: 'Direct access to 35,000+ product portfolio with competitive bulk pricing and reliable supply chain management.' 
              },
              { 
                title: 'Technology Integration', 
                description: 'Complete POS system, inventory management software, billing systems, and digital receipt infrastructure.' 
              },
              { 
                title: 'Training & Development', 
                description: 'Comprehensive training programs for pharmacy operations, regulatory compliance, and customer service excellence.' 
              },
              { 
                title: 'Marketing & Launch', 
                description: 'Grand opening campaigns, store branding, promotional materials, digital marketing support, and local advertising.' 
              },
              { 
                title: 'Operational Support', 
                description: 'Ongoing operational guidance, quality audits, compliance monitoring, and performance optimization assistance.' 
              },
              { 
                title: 'Discount Strategy', 
                description: '10-85% discount structure on all medicines to drive customer acquisition and retention with proven success.' 
              }
            ].map((item, index) => (
              <div key={index} className="hover-lift" style={{
                padding: '32px',
                background: 'var(--bg-primary)',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: index % 2 === 0 ? 'var(--brand-blue)' : 'var(--brand-green)',
                  color: 'white',
                  borderRadius: '0px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: '500',
                  marginBottom: '16px'
                }}>
                  {index + 1}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {item.title}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Care Pharmacy */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Why Partner With Care Pharmacy
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <div style={{
              padding: '40px',
              background: 'var(--bg-secondary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <div className="hero-medium" style={{ color: 'var(--brand-blue)', marginBottom: '16px' }}>
                Proven Track Record
              </div>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                190+ successfully operating outlets across India with strong customer loyalty and repeat business driving sustainable growth.
              </p>
            </div>
            <div style={{
              padding: '40px',
              background: 'var(--bg-secondary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <div className="hero-medium" style={{ color: 'var(--brand-green)', marginBottom: '16px' }}>
                Competitive Advantage
              </div>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Aggressive 10-85% discount strategy, comprehensive product portfolio, and established supply chain infrastructure.
              </p>
            </div>
            <div style={{
              padding: '40px',
              background: 'var(--bg-secondary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <div className="hero-medium" style={{ color: 'var(--brand-blue)', marginBottom: '16px' }}>
                Flexible Models
              </div>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Three distinct investment tiers (Silver, Gold, Platinum) catering to different investor profiles and involvement levels.
              </p>
            </div>
            <div style={{
              padding: '40px',
              background: 'var(--bg-secondary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <div className="hero-medium" style={{ color: 'var(--brand-green)', marginBottom: '16px' }}>
                Transparent Returns
              </div>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Clear profit projections with minimum guarantee options for FICO models ensuring predictable passive income streams.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI & Timeline */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '60px 40px',
            background: 'var(--bg-primary)',
            borderRadius: '0px',
            border: '2px solid var(--brand-green)'
          }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
              Investment Timeline & Returns
            </h2>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              Transparent commercials with proven ROI timelines and comprehensive support throughout your franchise journey.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '32px'
            }}>
              {[
                { label: 'Launch Timeline', value: '45-60 days', description: 'From agreement to grand opening' },
                { label: 'Break-even Period', value: '12-18 months', description: 'Typical break-even timeframe' },
                { label: 'ROI Achievement', value: '18-24 months', description: 'Full investment recovery' },
                { label: 'Long-term Growth', value: '5+ years', description: 'Sustainable profitability' }
              ].map((stat, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div className="heading-2" style={{ color: 'var(--brand-blue)', marginBottom: '8px' }}>
                    {stat.value}
                  </div>
                  <div className="body-regular" style={{ fontWeight: '500', marginBottom: '4px' }}>
                    {stat.label}
                  </div>
                  <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {stat.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Accordion type="single" collapsible>
              {faqFranchise.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="body-regular" style={{ textAlign: 'left' }}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            maxWidth: '700px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
              Ready to Start Your Pharmacy Business?
            </h2>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '32px'
            }}>
              Contact our franchise team for detailed information and onboarding support.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="tel:+918200886945" className="btn-primary">
                Call +91-8200886945
              </a>
              <Link to="/contact" className="btn-secondary">
                Submit Enquiry
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Franchise;